/**
 * Queue a Xero incremental import for the Profit period window, then wait until
 * the job finishes so P&L / invoices match the chip exactly.
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from './authenticatedFetch';

const POLL_MS = 2_000;
const MAX_WAIT_MS = 180_000;

export type SyncXeroForProfitPeriodResult =
  | { ok: true; jobId: string }
  | { ok: false; error: string };

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const t = setTimeout(() => resolve(), ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

/**
 * POST /ingestion/manage/incremental for Xero with an explicit dateRange
 * (same window as the Profit period filter), then poll until completed.
 */
export async function syncXeroForProfitPeriod(
  startDate: string,
  endDate: string,
  options?: { signal?: AbortSignal },
): Promise<SyncXeroForProfitPeriodResult> {
  const from = startDate.slice(0, 10);
  const to = endDate.slice(0, 10);
  if (!from || !to || from > to) {
    return { ok: false, error: 'Invalid Profit date range for Xero sync' };
  }

  const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.incremental(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sources: ['xero'],
      dateRange: { from, to },
    }),
    signal: options?.signal,
  });

  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    return {
      ok: false,
      error: data.message ?? data.error ?? `Xero sync failed to start (HTTP ${res.status})`,
    };
  }

  const started = (await res.json().catch(() => ({}))) as { jobId?: string; job_id?: string };
  const jobId = started.jobId ?? started.job_id;
  if (!jobId) {
    // Some deployments return 202 with job already tracked — still allow KPI reload.
    return { ok: true, jobId: '' };
  }

  const deadline = Date.now() + MAX_WAIT_MS;
  while (Date.now() < deadline) {
    if (options?.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const statusRes = await authenticatedFetch(API_ENDPOINTS.ingestionManage.jobStatus(jobId), {
      signal: options?.signal,
    });
    if (statusRes.ok) {
      const job = (await statusRes.json()) as { status?: string; error?: string };
      if (job.status === 'completed') return { ok: true, jobId };
      if (job.status === 'failed' || job.status === 'cancelled') {
        return {
          ok: false,
          error: job.error ?? `Xero sync ${job.status}`,
        };
      }
    }
    await sleep(POLL_MS, options?.signal);
  }

  return {
    ok: false,
    error: 'Xero sync timed out — try again or use Data Import',
  };
}
